import React, { useState } from "react";
import { Layout, Menu, Avatar, Dropdown } from "antd";
import type { MenuProps } from "antd";
import {
  UserOutlined,
  LogoutOutlined,
  AccountBookOutlined,
  GlobalOutlined,
  ProjectOutlined,
  TeamOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import { BiBuilding, BiHome } from "react-icons/bi";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { logout } from "../../../core/store/reducers/auth.slice";
import { RootState } from "../../types/common";
import {
  ROUTE_MANAGE_DEPARTMENT,
  ROUTE_MANAGE_STAFF,
  ROUTE_MANAGE_PROJECT,
  ROUTE_MILESTONES,
  ROUTE_MANAGE_VENDOR,
  ROUTE_BILLING_SUMMARY,
  ROUTE_ALLOCATION_REPORT,
  ROUTE_MANAGE_ACCOUNT,
  ROUTE_MANAGE_SUB_DEPARTMENT,
  ROUTE_MANAGE_FOREX,
} from "../../routes/private.routes";
import { allPermissions } from "../../constants/constants";
import "../../../assets/styles/sidebar.less";
import { useMsal } from "@azure/msal-react";

const { Sider } = Layout;

type MenuItem = Required<MenuProps>["items"][number];

function getItem(
  label: React.ReactNode,
  key: React.Key,
  icon?: React.ReactNode,
  children?: MenuItem[],
  permission?: string[]
): MenuItem {
  return {
    key,
    icon,
    children: children?.length ? children : undefined,
    label,
    permission,
  } as MenuItem;
}

const SidebarComponent: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { instance, accounts } = useMsal();
  const { user, myPermissions } = useSelector((state: RootState) => state.auth);
  const [collapsed, setCollapsed] = useState(false);

  const userPermissionSet = new Set(
    myPermissions?.map((p: any) => p.permission?.name) || []
  );
  const hasPermission = (perms?: string[]) => {
    if (!perms || perms.length === 0) return true;
    return perms.some((p) => userPermissionSet.has(p));
  };

  const menuItems: MenuItem[] = [
    getItem(
      "Staff",
      "staff",
      <TeamOutlined />,
      [
        getItem("Manage Staff", ROUTE_MANAGE_STAFF, null, undefined, [
          allPermissions.VIEW_STAFF,
        ]),
      ],
      [allPermissions.VIEW_STAFF]
    ),
    getItem(
      "Project",
      "project",
      <ProjectOutlined />,
      [
        getItem("Manage Project", ROUTE_MANAGE_PROJECT, undefined, undefined, [allPermissions.VIEW_PROJECT]),
        getItem("Milestones", ROUTE_MILESTONES, undefined, undefined, [allPermissions.VIEW_PROJECT]),
        getItem("Allocation Report", ROUTE_ALLOCATION_REPORT, undefined, undefined, [allPermissions.VIEW_PROJECT]),
        getItem("Billing Summary", ROUTE_BILLING_SUMMARY, undefined, undefined, [allPermissions.VIEW_BILLING_SUMMARY]),
      ],
      []
    ),
    getItem(
      "Settings",
      "Settings",
      <SettingOutlined />,
      [
        getItem("Manage Vendor", ROUTE_MANAGE_VENDOR, undefined, undefined, [allPermissions.VIEW_VENDOR]),
        getItem("Manage Client", ROUTE_MANAGE_ACCOUNT, undefined, undefined, [allPermissions.VIEW_ACCOUNT]),
        getItem("Manage Department", ROUTE_MANAGE_DEPARTMENT, undefined, undefined, [allPermissions.VIEW_DEPARTMENT]),
        getItem("Manage Sub Department", ROUTE_MANAGE_SUB_DEPARTMENT, undefined, undefined, [allPermissions.VIEW_SUBDEPARTMENT]),
        getItem("Manage Forex", ROUTE_MANAGE_FOREX, undefined, undefined, [allPermissions.VIEW_FOREX]), 
      ]
    ),
  ].filter((item: any) => hasPermission(item.permission)) as MenuItem[];

  // Filter children by permission
  const filteredItems = menuItems.map((item: any) => {
    if (item.children) {
      return {
        ...item,
        children: item.children.filter((child: any) =>
          hasPermission(child.permission)
        ),
      };
    }
    return item;
  }).filter((item: any) => !item.children || item.children.length > 0);

  const onMenuClick = ({ key }: { key: string }) => {
    if (key && key.startsWith("/")) navigate(key);
  };

  const profileMenuItems: MenuProps["items"] = [
    {
      key: "logout",
      icon: <LogoutOutlined />,
      label: "Logout",
      onClick: () => {
        instance.logoutRedirect({
          onRedirectNavigate: (url) => {
              // Return false to stop navigation to the MS sign-out page
              return false;
          }
        });  
        setTimeout(() => {
          dispatch(logout())
        }, 500);
      }
    },
  ];

  const selectedKey = location.pathname;

  const defaultOpenKeys = (() => {
    const path = location.pathname;
    if (path.startsWith("/manage-staff")) return ["staff"];
    if (path.startsWith("/manage-vendor")) return ["vendor"];
    if (path.startsWith("/manage-client") || path.startsWith("/manage-account")) return ["client"];
    if (path.startsWith("/manage-project") || path.startsWith("/milestones") || path.startsWith("/billing-summary")) return ["project"];
    if (path.startsWith("/manage-department") || path.startsWith("/manage-sub-department")) return ["department"];
    if (path.startsWith("/manage-forex")) return ["forex"];
    return ["staff", "project"];
  })();

  return (
    <Sider
      collapsible
      collapsed={collapsed}
      onCollapse={setCollapsed}
      width={260}
      collapsedWidth={72}
      className="app-sidebar"
      theme="dark"
    >
      <div className="sidebar-header">
        <div className="sidebar-logo text-center">
          {/* <img
            alt="logo"
            src="https://roboticodigital.com/assets/images/logo/robotico-logo(white)-01.svg"
            className="sidebar-logo-img"
          /> */}
          {!collapsed && <img
            alt="logo"
            src="https://roboticodigital.com/assets/images/logo/robotico-logo(white)-01.svg"
            className="sidebar-logo-img"
          />}
          {collapsed && <span className="sidebar-logo-text">RD</span>}
        </div>
      </div>

      <div className="sidebar-menu-wrap">
        <Menu
          mode="inline"
          selectedKeys={[selectedKey]}
          defaultOpenKeys={defaultOpenKeys.length ? defaultOpenKeys : ["staff", "project"]}
          items={filteredItems}
          onClick={onMenuClick}
          className="sidebar-menu"
        />
      </div>

      <div className="sidebar-footer">
        <Dropdown
          menu={{ items: profileMenuItems }}
          placement="topRight"
          trigger={["click"]}
        >
          <button className="sidebar-profile-trigger" type="button">
            <Avatar
              size={collapsed ? 36 : 40}
              icon={<UserOutlined />}
              className="sidebar-profile-avatar"
            />
            {!collapsed && (
              <div className="sidebar-profile-info">
                <span className="sidebar-profile-name">
                  {user?.fullName || "User"}
                </span>
                <span className="sidebar-profile-role">
                  {user?.user_name || ""}
                </span>
              </div>
            )}
          </button>
        </Dropdown>
      </div>
    </Sider>
  );
};

export default SidebarComponent;
